import assetTable from './float-assets.json';
import { buildScene, drawScene, lightMap, positions, FRAME, KINDS, LIGHT_MAP, type LightMap, type Place, type Scene, type SceneAsset } from './float-scene.mjs';

// THE FLOATING MATERIAL, LIVE ON THE PAGE (owner 2026-10-17). The scene and
// its drawing are the same file the server draws the downloads with
// (float-scene.mjs), so the page shows what the file has: material drifting
// slowly down, embers rising off a fire, smoke puffs rising and fading, rays
// sliding down the beam, bullet holes still.
//
// What only the page adds is the air of a cursor or finger ("the floating
// material could seemingly be disturbed by the air of the movement"): things
// near it are nudged along its path, gently, and settle back over a couple of
// seconds (2026-10-17: the first version swept the whole overlay off the
// screen). Smoke and the beam are not pushed.

type AssetRow = { src: string; pieces: number[][]; refWidth: number };
const ASSETS = assetTable as Record<string, AssetRow>;

export const FLOAT_KINDS = Object.keys(KINDS);

const images = new Map<string, Promise<HTMLImageElement>>();
function image(src: string): Promise<HTMLImageElement> {
  let p = images.get(src);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
    images.set(src, p);
  }
  return p;
}

export interface FloatFieldOptions {
  kinds: string[];
  amount?: string;
  /** The picker's small tiles: fewer, larger things so the kind reads at a glance. */
  preview?: boolean;
  seed?: number;
  /** The picture under the field (the picked picture or the cover's poster): the dust seen only in its light reads it. */
  lightFrom?: string;
  /** Each kind's own see-through slider, 0..1 (setOpacity changes it live). */
  opacity?: Record<string, number> | null;
  /** Where each kind sits ({ kind: { x, y, s } }; setPlace changes it live). */
  place?: Record<string, Place> | null;
  /** The frame's size (owner 2026-10-23: the cover drawn in the shape picked, 4:5, square, 16:9...); 1080x1920 when absent. */
  frame?: { w: number; h: number } | null;
}

/**
 * The picture's light at LIGHT_MAP's size, stretched to it as the server's sharp does (canvas.js lightPixels): the
 * picture given is the frame's own (the shape's still), so the map is read back by shares of the frame.
 */
async function readLight(src: string): Promise<LightMap | null> {
  try {
    const img = await image(src);
    const { w, h } = LIGHT_MAP;
    const c = Object.assign(document.createElement('canvas'), { width: w, height: h });
    const g = c.getContext('2d', { willReadFrequently: true });
    if (!g) return null;
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, 0, 0, w, h);
    return lightMap(g.getImageData(0, 0, w, h).data);
  } catch {
    // A picture that cannot be read (another site's, without permission): the dust shows everywhere.
    return null;
  }
}

/** A live field on `canvas` (sized to its box, 9:16): stir() from pointer moves, puff() on a tap, kick() on a bass hit, stop(). */
export function createFloatField(canvas: HTMLCanvasElement, opts: FloatFieldOptions) {
  const ctx = canvas.getContext('2d');
  let scene: Scene | null = null;
  let stopped = false;
  // The cursor's push on each thing: offset and speed, in frame pixels.
  let push: { x: number; y: number; vx: number; vy: number }[] = [];
  const kinds = opts.kinds.filter((k) => KINDS[k]);
  const F = opts.frame || FRAME;
  let opacity = opts.opacity || {};
  let place = opts.place || {};

  void (async () => {
    const assets: Record<string, SceneAsset> = {};
    for (const kind of kinds) {
      const src = KINDS[kind].src;
      if (!src || assets[src] || !ASSETS[src]) continue;
      try {
        const img = await image(ASSETS[src].src);
        assets[src] = { image: img, pieces: ASSETS[src].pieces, refWidth: ASSETS[src].refWidth };
      } catch {
        /* a picture that will not load: that kind is left out */
      }
    }
    const light = opts.lightFrom && kinds.some((k) => KINDS[k].lit) ? await readLight(opts.lightFrom) : null;
    if (stopped) return;
    scene = buildScene({
      light,
      kinds,
      amount: opts.amount,
      seed: opts.seed ?? 7,
      assets,
      preview: opts.preview,
      opacity,
      place,
      makeCanvas: (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h }),
      frame: F,
    });
    push = scene.items.map(() => ({ x: 0, y: 0, vx: 0, vy: 0 }));
  })();

  let w = 0;
  let h = 0;
  let scale = 1;
  let raf = 0;
  let last = performance.now();
  let visible = true;
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Speed (owner 2026-10-20: "the site is extremely laggy"): the downloads are 30 frames a second, so the page draws
  // no more than that; a picker's small tile draws 24 a second at the screen's own pixels (it is a few dozen wide).
  const frameMs = opts.preview ? 1000 / 24 : 1000 / 30;
  let lastDraw = 0;
  const fit = () => {
    const dpr = opts.preview ? 1 : Math.min(2, window.devicePixelRatio || 1);
    const box = canvas.getBoundingClientRect();
    w = Math.max(1, Math.round(box.width * dpr));
    h = Math.max(1, Math.round(box.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    // Cover-fit the frame (1080x1920, or the shape's) into the box.
    scale = Math.max(w / F.w, h / F.h);
  };

  const draw = (now: number) => {
    raf = requestAnimationFrame(draw);
    if (!ctx || !visible) return;
    if (now - lastDraw < frameMs - 2) return;
    lastDraw = now;
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (!scene) return;
    // The push settles: a spring home and drag, so a stirred thing drifts back within a couple of seconds.
    for (const p of push) {
      p.vx += -p.x * 3 * dt;
      p.vy += -p.y * 3 * dt;
      const drag = 1 - Math.min(0.95, 3.5 * dt);
      p.vx *= drag;
      p.vy *= drag;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    ctx.setTransform(scale, 0, 0, scale, (w - F.w * scale) / 2, (h - F.h * scale) / 2);
    drawScene(ctx as unknown as CanvasRenderingContext2D, scene, reduce ? 0 : now / 1000, { which: 'all', push: (i) => push[i] });
  };

  fit();
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
  ro?.observe(canvas);
  const io =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => {
          visible = e.isIntersecting;
          last = performance.now();
        })
      : null;
  io?.observe(canvas);
  raf = requestAnimationFrame(draw);

  /** Frame pixels from a client point. */
  const toFrame = (clientX: number, clientY: number) => {
    const box = canvas.getBoundingClientRect();
    const k = Math.max(box.width / F.w, box.height / F.h);
    return { x: (clientX - box.left - (box.width - F.w * k) / 2) / k, y: (clientY - box.top - (box.height - F.h * k) / 2) / k, k };
  };

  /** Nudge the things within `radius` of a point: `along` (frame px/s) is the air's motion, `out` pushes away from the point. */
  const nudge = (px: number, py: number, along: { x: number; y: number }, out: number, radius: number) => {
    if (!scene) return;
    const now = positions(scene, performance.now() / 1000);
    now.forEach((p, i) => {
      if (!p.moves || p.a < 0.02) return;
      const q = push[i];
      const dx = p.x + q.x - px;
      const dy = p.y + q.y - py;
      const d = Math.hypot(dx, dy);
      if (d > radius) return;
      const f = (1 - d / radius) * (0.5 + 0.5 * p.z);
      q.vx += (along.x + (dx / (d || 1)) * out) * f;
      q.vy += (along.y + (dy / (d || 1)) * out) * f;
      // Never further than a short way: the air moves things, it does not blow them away.
      const sp = Math.hypot(q.vx, q.vy);
      if (sp > 260) {
        q.vx *= 260 / sp;
        q.vy *= 260 / sp;
      }
    });
  };

  return {
    /** The air of a moving cursor or finger: client movement in CSS pixels since the last move. */
    stir(clientX: number, clientY: number, moveX: number, moveY: number) {
      const p = toFrame(clientX, clientY);
      const vx = Math.max(-40, Math.min(40, moveX / p.k));
      const vy = Math.max(-40, Math.min(40, moveY / p.k));
      if (!vx && !vy) return;
      nudge(p.x, p.y, { x: vx * 4, y: vy * 4 }, Math.hypot(vx, vy) * 1.5, 220);
    },
    /** A tap: a small puff of air out from the finger. */
    puff(clientX: number, clientY: number, strength = 1) {
      const p = toFrame(clientX, clientY);
      nudge(p.x, p.y, { x: 0, y: 0 }, 200 * strength, 300);
    },
    /** A bass hit: everything shivers a little outward from the middle. */
    kick(strength: number) {
      if (reduce) return;
      nudge(F.w / 2, F.h / 2, { x: 0, y: 0 }, 60 * strength, 4000);
    },
    /** Each kind's see-through slider, live (no new scene: the things stay where they are). */
    setOpacity(next: Record<string, number> | null | undefined) {
      opacity = next || {};
      if (scene) scene.opacity = opacity;
    },
    /** Where each kind sits, live (the things stay where they are in their own frame; the frame moves). */
    setPlace(next: Record<string, Place> | null | undefined) {
      place = next || {};
      if (scene) scene.place = place;
    },
    stop() {
      stopped = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
      // Nothing of this field may stay on screen (2026-10-17: a deselected smoke stayed behind).
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    },
  };
}

export type FloatField = ReturnType<typeof createFloatField>;
